import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';

// Hooks
import useIsomorphicLayoutEffect from 'hooks/use-isomorphic-layout-effect';

// Utils
import AVAILABLE_LOCALES from 'utils/locales';
import bemify from 'utils/bemify';
import convertStringToKebabCase from 'utils/convert-string-to-kebab-case';
import PRODUCT_TYPES from 'utils/product-types';

// Components
import NoResultsSection from 'components/no-results-section';
import ProductCard from 'components/product-card';
import ProductModal from 'components/product-modal';

// Assets
import NoResultsImage from 'assets/images/search-no-results/no-results.svg';

// Styles
import stylesModule from './styles.module.scss';


const styles = bemify(stylesModule);

const ProductsListing = ({
  allowAddToCart = true,
  className = null,
  menuSections = [],
  onCurrentScrollSectionIdChange = () => {},
  onFilteredMenuSectionsUpdate = () => {},
  onProductAddToCart = () => {},
  reduceGrid = false,
  restaurantCurrency = null,
  searchString = null,
  selectedAllergens = null,
  selectedLocale = null,
  selectedMaximumPrice = null,
  selectedMinimumPrice = null,
  selectedProductType = null,
  translations = null
}) => {
  // States
  const [selectedProductDetails, setSelectedProductDetails] = useState(null);
  const [showProductModal, setShowProductModal] = useState(false);

  const allergensTranslations = translations?.filters?.allergens;

  // Refs
  const titleRefs = useRef([]);

  // Computed
  const filteredMenuSections = useMemo(() => {
    const newFilteredMenuSections = [];

    menuSections.forEach((menuSection) => {
      const filteredProducts = menuSection.menuSectionProducts
        .map((sectionProduct) => {
          const product = sectionProduct.product || {};
          const translationsObject = product.translations || {};

          const title = translationsObject?.[selectedLocale]?.title ||
            product.fallbackTranslations?.title ||
            '';

          const description = translationsObject?.[selectedLocale]?.description ||
            product.fallbackTranslations?.description ||
            '';

          const ingredients = translationsObject?.[selectedLocale]?.ingredients ||
            product.fallbackTranslations?.ingredients ||
            '';

          return {
            ...product,
            description,
            ingredients,
            title
          };
        })
        .filter((product) => {
          if (
            Array.isArray(selectedAllergens) &&
            selectedAllergens.length &&
            !product.allergens.some(allergen => selectedAllergens.includes(allergen))
          ) return false;

          if (selectedMaximumPrice && product.price > selectedMaximumPrice) return false;
          if (selectedMinimumPrice && product.price < selectedMinimumPrice) return false;

          if (
            selectedProductType !== PRODUCT_TYPES.ALL &&
            ((selectedProductType === PRODUCT_TYPES.DISH && product.isBeverage) ||
            (selectedProductType === PRODUCT_TYPES.BEVERAGE && !product.isBeverage))
          ) return false;

          const upperCasedSearchString = (searchString || '').toUpperCase();

          return (product?.title || '').toUpperCase().includes(upperCasedSearchString) ||
            (product?.description || '').toUpperCase().includes(upperCasedSearchString) ||
            (product?.ingredients || '').toUpperCase().includes(upperCasedSearchString);
        });

      const sectionTranslations = menuSection.translations || {};
      const menuSectionTranslations = sectionTranslations[selectedLocale] ||
          sectionTranslations[Object.keys(sectionTranslations)[0]] ||
          null;

      if (filteredProducts.length) {
        newFilteredMenuSections.push({
          id: menuSection.id,
          name: menuSectionTranslations?.name,
          products: filteredProducts,
          slug: convertStringToKebabCase(menuSectionTranslations?.name) || `section-${menuSection.id}`
        });
      }
    });

    return newFilteredMenuSections;
  }, [
    menuSections,
    searchString,
    selectedAllergens,
    selectedLocale,
    selectedMaximumPrice,
    selectedMinimumPrice,
    selectedProductType
  ]);

  // Methods
  const handleAddToCart = (productId, quantity) => {
    onProductAddToCart(productId, quantity);
  };

  const handleProductCardClick = (productDetails) => {
    setShowProductModal(true);

    setSelectedProductDetails(productDetails);
  };

  const handleProductModalClose = () => {
    setShowProductModal(false);
  };

  // Effects
  useEffect(() => {
    onFilteredMenuSectionsUpdate(filteredMenuSections);
  }, [filteredMenuSections, onFilteredMenuSectionsUpdate]);

  useIsomorphicLayoutEffect(() => {
    const { current: titles } = titleRefs;

    // leave a 30px window to detect intersections
    // The top margin is -140px to match the mobile layout's topbar height, plus a 4px margin
    // The bottom margin is the -(window height - 170), to leave a 30px window that will detect intersections
    const bottomObserverMargin = window.innerHeight - 170;
    const rootMargin = `-140px 0px -${bottomObserverMargin}px 0px`;

    const observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          onCurrentScrollSectionIdChange(entry.target.getAttribute('data-id'));
        }
      }
    }, {
      rootMargin,
      threshold: 0
    });

    if (Array.isArray(titles) && titles.length) {
      titles.forEach((title) => {
        if (title) observer.observe(title);
      });
    }

    return () => {
      observer.disconnect();
    };
  }, [onCurrentScrollSectionIdChange]);

  if (!Array.isArray(filteredMenuSections) || !filteredMenuSections.length) {
    return (
      <NoResultsSection
        className={styles.restaurantMenu__section}
        description={translations.search.noResults.description}
        imageComponent={<NoResultsImage />}
        title={translations.search.noResults.title}
      />
    );
  }

  return (
    <div className={`${styles.productsListing}${className ? ` ${className}` : ''}`}>
      {filteredMenuSections.map((section, index) => (
        <div key={section.slug}>
          {/* Used to offset the anchor scroll with CSS */}
          <div
            className={styles.productsListing__anchorOffsetDiv}
            id={section.slug}
          />
          <div
            data-id={section.slug}
          >
            <h5
              ref={(element) => {
                titleRefs.current[index] = element;
              }}
              className={styles.productsListing__sectionTitle}
              data-id={section.slug}
            >
              {section.name}
            </h5>
            <div className={`${styles.productsListing__productsGrid}${reduceGrid ? ` ${styles.productsListing__productsGrid_thin}` : ''}`}>
              {section.products.map(product => (
                <ProductCard
                  key={product.entityId}
                  allergensTranslations={allergensTranslations}
                  className={styles.productsListing__productCard}
                  paddingSize="md"
                  productDetails={product}
                  restaurantCurrency={restaurantCurrency}
                  selectedLocale={selectedLocale}
                  translations={translations.actions}
                  onAddToCart={allowAddToCart ? quantity => handleAddToCart(product.entityId, quantity) : null}
                  onClick={() => handleProductCardClick(product)}
                />
              ))}
            </div>
          </div>
        </div>
      ))}
      <ProductModal
        allergensTranslations={allergensTranslations}
        productDetails={selectedProductDetails}
        restaurantCurrency={restaurantCurrency}
        selectedLocale={selectedLocale}
        show={showProductModal}
        translations={translations}
        onAddToCart={allowAddToCart ? quantity => handleAddToCart(selectedProductDetails?.entityId, quantity) : null}
        onClose={handleProductModalClose}
      />
    </div>
  );
};


ProductsListing.propTypes = {
  allowAddToCart: PropTypes.bool,
  className: PropTypes.string,
  menuSections: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string
  })),
  onCurrentScrollSectionIdChange: PropTypes.func,
  onFilteredMenuSectionsUpdate: PropTypes.func,
  onProductAddToCart: PropTypes.func,
  reduceGrid: PropTypes.bool,
  restaurantCurrency: PropTypes.string,
  searchString: PropTypes.string,
  selectedAllergens: PropTypes.arrayOf(PropTypes.string),
  selectedLocale: PropTypes.oneOf(Object.keys(AVAILABLE_LOCALES)),
  selectedMaximumPrice: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  selectedMinimumPrice: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  selectedProductType: PropTypes.string,
  translations: PropTypes.shape({
    search: PropTypes.shape({
      noResults: PropTypes.shape({
        description: PropTypes.string,
        title: PropTypes.string
      })
    })
  })
};

ProductsListing.displayName = 'ProductsListing';

export default ProductsListing;
